import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-din-paper.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { finalizePDF } from '@adornis/print/client/finalize-pdf.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/fonts.js';
import '@chancenwerk/design/index.js';
import { html, type PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators.js';

const sections: Array<{
  title: string;
  content: string[];
}> = [
  {
    title: 'Abmeldung, Aufsichtspflicht und Erreichbarkeit',
    content: [
      'Sie müssen Ihr Kind telefonisch entschuldigen, wenn es nicht zur Betreuung kommen kann. Dazu rufen Sie die Ansprechperson aus dem Schulteam an. Die Telefonnummer steht auf dem Chancenwerk-Infoblatt. Die Aufsichtspflicht der Verantwortlichen von Chancenwerk e.V. beginnt und endet mit den bekannten Förderzeiten. Innerhalb der Aufsichtspflichtgrenzen muss den Anweisungen der Verantwortlichen Folge geleistet werden. Bei unerlaubtem Entfernen Ihres Kindes von der Gruppe oder unentschuldigtem Fehlen endet die Aufsichtspflicht der Verantwortlichen sofort. Die Aufsichtspflicht endet auch, wenn die Verantwortlichen Ihr Kind – bei Minderjährigen nach versuchter telefonischer Rücksprache – wegen Auffälligkeiten frühzeitig nach Hause schicken müssen. Sie verpflichten sich zu diesem Zweck, dem Verein aktuelle Telefonnummern mitzuteilen und achten darauf, dass eine zur Aufsicht Ihres Kindes befähigte Person während der Förderzeiten immer erreichbar ist.',
    ],
  },
  {
    title: 'Entbindung von der Schweigepflicht',
    content: [
      'Chancenwerk e.V. möchte Ihr Kind bestmöglich fördern. Dabei hilft dem Schulteam der Austausch mit den Lehrkräften Ihres Kindes im Rahmen einer pädagogischen Zusammenarbeit, z. B. bezogen auf (Zeugnis-) Noten, Lernstand, Sozialverhalten. Aus diesem Grund entbinden Sie die Verantwortlichen von Chancenwerk e.V. und die Lehrkräfte Ihres Kindes während der Laufzeit und zum Zwecke des Lernförderangebots von der Schweigepflicht. Sie können diese Entbindung von der Schweigepflicht jederzeit ohne Angabe von Gründen schriftlich widerrufen, z. B. per Brief oder E-Mail.',
    ],
  },
  {
    title: 'Daten zu Leistungsschwächen',
    content: [
      'Mit Ihrer Unterschrift auf dem Formular, das wir Ihnen postalisch zusenden, willigen Sie ein, dass Chancenwerk e.V. im Rahmen des Lernförderangebots die Daten Ihres Kindes zu eventuellen Teilleistungsschwächen verarbeiten darf. Diese Einwilligung ist freiwillig. Sie können diese Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen. Hierzu genügt ein schriftlicher Widerruf an Chancenwerk e.V., z. B. per E-Mail oder Brief.',
    ],
  },
  {
    title: 'Kündigung',
    content: [
      'Sie können die Mitgliedschaft jederzeit mit einer Frist von 14 Tagen zum Monatsende kündigen. Die Kündigung muss schriftlich erfolgen, z. B. per Brief oder E-Mail. Die Adresse für Widerruf oder Kündigung finden Sie auf dem Chancenwerk-Infoblatt. Chancenwerk e.V. behält sich vor, die Mitgliedschaft zu kündigen, falls das Lernförderangebot nicht mehr angeboten werden kann.',
    ],
  },
  {
    title: 'Verwendung von Foto- und Videoaufnahmen',
    content: [
      'Unserer Vereinsarbeit würde es sehr helfen, wenn wir für die Öffentlichkeitsarbeit Foto- und Videoaufnahmen Ihres Kindes verwenden dürfen. Für diese Datenverarbeitung ist datenschutzrechtlich eine Einwilligung von allen Erziehungsberechtigten und von Ihrem Kind erforderlich. Diese Einwilligung ist freiwillig und für die Teilnahme Ihres Kindes am Lernförderangebot nicht erforderlich.',
      'Unsere Arbeit wird zu einem großen Teil durch Unterstützer:innen finanziert. Um diese und auch andere Interessierte extern über unsere Vereinsarbeit bspw. in den sozialen Medien oder auf unserer Homepage informieren zu können, nutzen wir Foto- und Videoaufnahmen. Diese Aufnahmen erfolgen im Rahmen von folgenden Aktionen: Freizeitaktion vor den Sommerferien, Winteraktion, Osteraktion, Schuljahresstart, Besuche von Presse, Workshops, Besuche von Stiftungen. Soweit Sie die Einwilligungserklärung (die wir Ihnen postalisch zusenden) abgeben, dürfen wir diese Aufnahmen in Printmedien, auf unserer Homepage sowie auf unseren Präsenzen in den sozialen Medien (derzeit Instagram, Facebook, LinkedIn) veröffentlichen sowie die Aufnahmen zu diesem Zweck an Unterstützer:innen und Presse weitergeben. Sonstige Aufnahmen, die wir bspw. zu internen Zwecken während der Lernförderung anfertigen, veröffentlichen wir hingegen nur dann, wenn Sie und Ihr Kind uns dazu eine gesonderte Einwilligung erteilt haben. Sie können Ihre Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen. Hierzu genügt ein schriftlicher Widerruf an Chancenwerk e.V., z. B. per E-Mail oder Brief.',
    ],
  },
  {
    title: 'Evaluation',
    content: [
      'Wir wollen Ihr Kind gut unterstützen und es soll sich bei uns wohlfühlen. Deshalb möchten wir unsere Arbeit im Rahmen der Evaluation regelmäßig überprüfen und verbessern. Zu diesem Zweck möchten wir Ihr Kind und Sie ein- bis zweimal im Jahr online befragen. Die Befragung Ihres Kindes erfolgt während des Lernförderangebots. Als Erziehungsberechtigte erhalten Sie einen Link zu einer Online-Befragung per E-Mail. Natürlich werden die erhobenen Daten vertraulich behandelt und die Teilnahme an der Evaluation ist freiwillig.',
    ],
  },
];

sections.map(section => html`<br /><b>${section.title}</b>`);

/**
 * @customElement chancen-component-anmeldung-consent
 */
@customElement('chancen-component-anmeldung-consent')
export class ChancenComponentAnmeldungConsent extends FormField<boolean> {
  override styles() {
    return [
      ...super.styles(),
      {
        '@media print': {
          'cr-text': {
            fontSize: '12px',
            lineHeight: '14px',
          },
          'cr-h3': {
            fontSize: '16px',
            lineHeight: '20px',
          },
        },
      },
    ];
  }

  @property({ attribute: false }) public translation = new TranslationController(this);

  @property({ attribute: false }) public print = true;

  protected override firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);

    requestAnimationFrame(() => finalizePDF());
  }

  override render() {
    return html`
      <x-din-paper size="${this.print ? 'a4' : ''}" margin=${this.print ? this.spacing.xl : ''}>
        <cr-flex space="md" padding=${this.print ? 'sm' : ''}>
          <cr-h3 ${css({ padding: `0 ${this.spacing.sm}` })}>
            Informationen zur Mitgliedschaft (diese erhalten Sie auch nochmal in der Bestätigungsmail)
          </cr-h3>
          <cr-flex
            crossaxis-center
            ${css({
              borderRadius: '0 60px',
              overflow: 'hidden',
              border: `${this.spacing.xxs} solid ${this.colors.tone.error}`,
              background: this.colors.tone.error,
            })}
          >
            <cr-flex
              space="md"
              padding="md"
              ${css({
                borderRadius: `0 ${this.spacing.xs}`,
                overflow: 'hidden',
                background: '#F4E2DF',
              })}
            >
              <cr-text
                ${css({
                  opacity: '.87',
                })}
              >
                ${sections.map(
                  section => html`
                    <b>${section.title}</b> <br />
                    ${section.content.map(part => html`${part}<br /><br />`)}
                  `,
                )}

                <b>Datenschutzrechtliche Informationen</b> <br />

                Informationen zur Verarbeitung der personenbezogenen Daten von Ihnen und Ihrem Kind im Rahmen des
                Lernförderangebots finden Sie unter:
                <cr-link href="https://www.chancenwerk.org/datenschutzinformation/n8e26s" target="_blank">
                  www.chancenwerk.org/datenschutzinformation/n8e26s
                </cr-link>
              </cr-text>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </x-din-paper>
    `;
  }
}
